import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  CheckCircle,
  Circle,
  Clock,
  FileText,
  Fingerprint,
  Video,
  Award,
  Package,
  AlertCircle,
  Calendar,
  MapPin,
  User,
  ArrowRight,
  Download,
  ExternalLink,
  ChevronDown,
  ChevronUp
} from 'lucide-react';
import { getVisaApplication, getVisaApplicationDocuments } from '../../../api/educentialApi';

const VisaStatusTracker = () => {
  const { applicationId } = useParams();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [application, setApplication] = useState(null);
  const [documents, setDocuments] = useState([]);
  const [error, setError] = useState('');
  const [showHistory, setShowHistory] = useState(false);

  useEffect(() => {
    fetchApplicationData();
  }, [applicationId]);

  const fetchApplicationData = async () => {
    try {
      setLoading(true);
      const [appResponse, docsResponse] = await Promise.all([
        getVisaApplication(applicationId),
        getVisaApplicationDocuments(applicationId)
      ]);

      if (appResponse.success && appResponse.data) {
        setApplication(appResponse.data);
      }

      if (docsResponse.success && docsResponse.data) {
        setDocuments(docsResponse.data);
      }
    } catch (err) {
      console.error('Error fetching visa application:', err);
      setError('Failed to load visa application details. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Define visa processing stages
  const visaStages = [
    {
      key: 'APPLICATION_SUBMITTED',
      label: 'Application Submitted',
      icon: FileText,
      description: 'Your visa application has been submitted'
    },
    {
      key: 'DOCUMENT_VERIFICATION',
      label: 'Document Verification',
      icon: CheckCircle,
      description: 'Documents are being verified by visa authorities'
    },
    {
      key: 'BIOMETRIC_SCHEDULED',
      label: 'Biometric Appointment',
      icon: Fingerprint,
      description: 'Biometric data collection scheduled'
    },
    {
      key: 'INTERVIEW_SCHEDULED',
      label: 'Interview Scheduled',
      icon: Video,
      description: 'Visa interview appointment scheduled'
    },
    {
      key: 'DECISION_PENDING',
      label: 'Decision Pending',
      icon: Clock,
      description: 'Waiting for visa decision'
    },
    {
      key: 'DECISION_MADE',
      label: 'Decision Made',
      icon: Award,
      description: 'Visa decision has been made'
    },
    {
      key: 'VISA_ISSUED',
      label: 'Visa Issued',
      icon: Package,
      description: 'Visa has been issued'
    },
    {
      key: 'VISA_COLLECTED',
      label: 'Visa Collected',
      icon: CheckCircle,
      description: 'Visa passport collected'
    }
  ];

  const getCurrentStageIndex = () => {
    if (!application) return 0;
    return visaStages.findIndex(stage => stage.key === application.currentStage);
  };

  const getStatusColor = (status) => {
    const colors = {
      DRAFT: 'bg-gray-100 text-gray-800',
      SUBMITTED: 'bg-blue-100 text-blue-800',
      UNDER_REVIEW: 'bg-yellow-100 text-yellow-800',
      DOCUMENTS_REQUESTED: 'bg-orange-100 text-orange-800',
      APPROVED: 'bg-green-100 text-green-800',
      REJECTED: 'bg-red-100 text-red-800',
      WITHDRAWN: 'bg-gray-100 text-gray-800'
    };
    return colors[status] || 'bg-gray-100 text-gray-800';
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const formatDateTime = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getNextSteps = () => {
    if (!application) return [];

    const steps = [];

    // Based on current status and stage
    if (application.status === 'DOCUMENTS_REQUESTED') {
      steps.push({
        title: 'Upload Required Documents',
        description: 'Please upload the requested documents to proceed with your application',
        action: () => navigate(`/visa/application/${applicationId}/documents`),
        actionLabel: 'Upload Documents',
        urgent: true
      });
    }

    if (application.currentStage === 'DOCUMENT_VERIFICATION') {
      const pendingDocs = documents.filter(doc => doc.status === 'PENDING');
      if (pendingDocs.length > 0) {
        steps.push({
          title: 'Document Verification in Progress',
          description: `${pendingDocs.length} document(s) are currently being verified`,
          urgent: false
        });
      }
    }

    if (application.biometricAppointment && !application.biometricAppointment.completed) {
      steps.push({
        title: 'Attend Biometric Appointment',
        description: `Scheduled for ${formatDateTime(application.biometricAppointment.date)}`,
        urgent: true
      });
    }

    if (application.currentStage === 'DECISION_MADE' && application.status === 'APPROVED') {
      steps.push({
        title: 'Collect Your Visa',
        description: 'Your visa is ready for collection',
        urgent: true
      });
    }

    if (steps.length === 0 && application.status === 'SUBMITTED') {
      steps.push({
        title: 'No Action Required',
        description: 'Your application is being processed. We will notify you of any updates.',
        urgent: false
      });
    }

    return steps;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-indigo-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading visa application status...</p>
        </div>
      </div>
    );
  }

  if (error || !application) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center max-w-md">
          <AlertCircle className="h-16 w-16 text-red-500 mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Error Loading Application</h2>
          <p className="text-gray-600 mb-6">{error || 'Application not found'}</p>
          <button
            onClick={() => navigate('/services/visa')}
            className="px-6 py-3 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700"
          >
            Back to Visa Services
          </button>
        </div>
      </div>
    );
  }

  const currentStageIndex = getCurrentStageIndex();
  const nextSteps = getNextSteps();

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="bg-white rounded-lg shadow-md p-6 mb-6">
          <div className="flex items-start justify-between">
            <div>
              <h1 className="text-3xl font-bold text-gray-900 mb-2">
                Visa Application Status
              </h1>
              <p className="text-gray-600 mb-4">
                Track your {application.country} {application.visaType} application
              </p>
              <div className="flex items-center space-x-4">
                <div className="flex items-center">
                  <FileText className="h-5 w-5 text-gray-400 mr-2" />
                  <span className="text-sm text-gray-600">
                    Ref: <span className="font-mono font-semibold">{application.applicationRef}</span>
                  </span>
                </div>
                <div className="flex items-center">
                  <Calendar className="h-5 w-5 text-gray-400 mr-2" />
                  <span className="text-sm text-gray-600">
                    Submitted: {formatDate(application.submittedAt)}
                  </span>
                </div>
              </div>
            </div>
            <div>
              <span className={`px-4 py-2 rounded-full text-sm font-semibold ${getStatusColor(application.status)}`}>
                {application.status.replace('_', ' ')}
              </span>
            </div>
          </div>
        </div>

        {/* Progress Timeline */}
        <div className="bg-white rounded-lg shadow-md p-6 mb-6">
          <h2 className="text-xl font-bold text-gray-900 mb-6">Application Progress</h2>

          <div className="space-y-6">
            {visaStages.map((stage, index) => {
              const Icon = stage.icon;
              const isCompleted = index < currentStageIndex;
              const isCurrent = index === currentStageIndex;
              const isPending = index > currentStageIndex;

              return (
                <div key={stage.key} className="flex items-start">
                  {/* Icon */}
                  <div className="flex-shrink-0">
                    <div className={`
                      w-12 h-12 rounded-full flex items-center justify-center
                      ${isCompleted ? 'bg-green-100' : isCurrent ? 'bg-indigo-100' : 'bg-gray-100'}
                    `}>
                      <Icon className={`
                        h-6 w-6
                        ${isCompleted ? 'text-green-600' : isCurrent ? 'text-indigo-600' : 'text-gray-400'}
                      `} />
                    </div>
                  </div>

                  {/* Content */}
                  <div className="ml-4 flex-1">
                    <div className="flex items-center justify-between">
                      <h3 className={`
                        text-lg font-semibold
                        ${isCompleted ? 'text-green-700' : isCurrent ? 'text-indigo-700' : 'text-gray-500'}
                      `}>
                        {stage.label}
                      </h3>
                      {isCompleted && (
                        <CheckCircle className="h-5 w-5 text-green-600" />
                      )}
                      {isCurrent && (
                        <Clock className="h-5 w-5 text-indigo-600 animate-pulse" />
                      )}
                    </div>
                    <p className={`
                      text-sm mt-1
                      ${isCurrent ? 'text-gray-700' : 'text-gray-500'}
                    `}>
                      {stage.description}
                    </p>
                  </div>

                  {/* Connector Line */}
                  {index < visaStages.length - 1 && (
                    <div className="absolute left-6 ml-0 mt-12 h-12 w-0.5 bg-gray-200" />
                  )}
                </div>
              );
            })}
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Next Steps */}
          <div className="bg-white rounded-lg shadow-md p-6">
            <h2 className="text-xl font-bold text-gray-900 mb-4">Next Steps</h2>

            {nextSteps.length > 0 ? (
              <div className="space-y-4">
                {nextSteps.map((step, index) => (
                  <div
                    key={index}
                    className={`
                      p-4 rounded-lg border-l-4
                      ${step.urgent ? 'border-orange-500 bg-orange-50' : 'border-blue-500 bg-blue-50'}
                    `}
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <h3 className="font-semibold text-gray-900 mb-1">{step.title}</h3>
                        <p className="text-sm text-gray-600">{step.description}</p>
                      </div>
                      {step.urgent && (
                        <AlertCircle className="h-5 w-5 text-orange-500 flex-shrink-0 ml-2" />
                      )}
                    </div>
                    {step.action && (
                      <button
                        onClick={step.action}
                        className="mt-3 flex items-center text-sm font-medium text-indigo-600 hover:text-indigo-700"
                      >
                        {step.actionLabel}
                        <ArrowRight className="h-4 w-4 ml-1" />
                      </button>
                    )}
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-gray-600">No pending actions at this time.</p>
            )}
          </div>

          {/* Application Details */}
          <div className="bg-white rounded-lg shadow-md p-6">
            <h2 className="text-xl font-bold text-gray-900 mb-4">Application Details</h2>

            <div className="space-y-4">
              <div>
                <label className="text-sm font-medium text-gray-500">Country</label>
                <p className="text-gray-900 font-semibold">{application.country}</p>
              </div>

              <div>
                <label className="text-sm font-medium text-gray-500">Visa Type</label>
                <p className="text-gray-900 font-semibold">{application.visaType}</p>
              </div>

              <div>
                <label className="text-sm font-medium text-gray-500">Applicant Name</label>
                <p className="text-gray-900 font-semibold">
                  {application.personalInfo?.fullName || 'N/A'}
                </p>
              </div>

              {application.educationDetails?.universityName && (
                <div>
                  <label className="text-sm font-medium text-gray-500">University</label>
                  <p className="text-gray-900 font-semibold">
                    {application.educationDetails.universityName}
                  </p>
                </div>
              )}

              {application.educationDetails?.courseName && (
                <div>
                  <label className="text-sm font-medium text-gray-500">Course</label>
                  <p className="text-gray-900 font-semibold">
                    {application.educationDetails.courseName}
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Biometric Appointment */}
        {application.biometricAppointment && (
          <div className="bg-white rounded-lg shadow-md p-6 mt-6">
            <div className="flex items-center mb-4">
              <Fingerprint className="h-6 w-6 text-indigo-600 mr-2" />
              <h2 className="text-xl font-bold text-gray-900">Biometric Appointment</h2>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="text-sm font-medium text-gray-500 flex items-center">
                  <Calendar className="h-4 w-4 mr-1" />
                  Date & Time
                </label>
                <p className="text-gray-900 font-semibold mt-1">
                  {formatDateTime(application.biometricAppointment.date)}
                </p>
              </div>

              <div>
                <label className="text-sm font-medium text-gray-500 flex items-center">
                  <MapPin className="h-4 w-4 mr-1" />
                  Location
                </label>
                <p className="text-gray-900 font-semibold mt-1">
                  {application.biometricAppointment.location || 'TBD'}
                </p>
              </div>

              <div>
                <label className="text-sm font-medium text-gray-500">Status</label>
                <p className={`
                  mt-1 font-semibold
                  ${application.biometricAppointment.completed ? 'text-green-600' : 'text-orange-600'}
                `}>
                  {application.biometricAppointment.completed ? 'Completed' : 'Scheduled'}
                </p>
              </div>
            </div>

            {application.biometricAppointment.confirmationNumber && (
              <div className="mt-4 p-3 bg-gray-50 rounded-lg">
                <label className="text-sm font-medium text-gray-500">Confirmation Number</label>
                <p className="text-gray-900 font-mono font-semibold">
                  {application.biometricAppointment.confirmationNumber}
                </p>
              </div>
            )}
          </div>
        )}

        {/* Decision Information */}
        {application.currentStage === 'DECISION_MADE' && application.decision && (
          <div className={`
            rounded-lg shadow-md p-6 mt-6
            ${application.status === 'APPROVED' ? 'bg-green-50 border-2 border-green-200' : 'bg-red-50 border-2 border-red-200'}
          `}>
            <div className="flex items-center mb-4">
              <Award className={`h-6 w-6 mr-2 ${application.status === 'APPROVED' ? 'text-green-600' : 'text-red-600'}`} />
              <h2 className="text-xl font-bold text-gray-900">Visa Decision</h2>
            </div>

            <div className="space-y-4">
              <div>
                <label className="text-sm font-medium text-gray-500">Decision</label>
                <p className={`
                  text-2xl font-bold
                  ${application.status === 'APPROVED' ? 'text-green-700' : 'text-red-700'}
                `}>
                  {application.status}
                </p>
              </div>

              {application.decisionDate && (
                <div>
                  <label className="text-sm font-medium text-gray-500">Decision Date</label>
                  <p className="text-gray-900 font-semibold">
                    {formatDate(application.decisionDate)}
                  </p>
                </div>
              )}

              {application.decision.notes && (
                <div>
                  <label className="text-sm font-medium text-gray-500">Notes</label>
                  <p className="text-gray-900">{application.decision.notes}</p>
                </div>
              )}

              {application.status === 'APPROVED' && application.decision.visaValidFrom && (
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="text-sm font-medium text-gray-500">Valid From</label>
                    <p className="text-gray-900 font-semibold">
                      {formatDate(application.decision.visaValidFrom)}
                    </p>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-gray-500">Valid Until</label>
                    <p className="text-gray-900 font-semibold">
                      {formatDate(application.decision.visaValidUntil)}
                    </p>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Document Summary */}
        <div className="bg-white rounded-lg shadow-md p-6 mt-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-xl font-bold text-gray-900">Documents</h2>
            <button
              onClick={() => navigate(`/visa/application/${applicationId}/documents`)}
              className="text-indigo-600 hover:text-indigo-700 font-medium text-sm flex items-center"
            >
              Manage Documents
              <ExternalLink className="h-4 w-4 ml-1" />
            </button>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="text-center p-4 bg-gray-50 rounded-lg">
              <p className="text-3xl font-bold text-gray-900">{documents.length}</p>
              <p className="text-sm text-gray-600 mt-1">Total Uploaded</p>
            </div>
            <div className="text-center p-4 bg-green-50 rounded-lg">
              <p className="text-3xl font-bold text-green-600">
                {documents.filter(d => d.status === 'APPROVED').length}
              </p>
              <p className="text-sm text-gray-600 mt-1">Verified</p>
            </div>
            <div className="text-center p-4 bg-yellow-50 rounded-lg">
              <p className="text-3xl font-bold text-yellow-600">
                {documents.filter(d => d.status === 'PENDING').length}
              </p>
              <p className="text-sm text-gray-600 mt-1">Under Review</p>
            </div>
            <div className="text-center p-4 bg-red-50 rounded-lg">
              <p className="text-3xl font-bold text-red-600">
                {documents.filter(d => d.status === 'REJECTED').length}
              </p>
              <p className="text-sm text-gray-600 mt-1">Rejected</p>
            </div>
          </div>
        </div>

        {/* Status History */}
        <div className="bg-white rounded-lg shadow-md p-6 mt-6">
          <button
            onClick={() => setShowHistory(!showHistory)}
            className="flex items-center justify-between w-full"
          >
            <h2 className="text-xl font-bold text-gray-900">Status History</h2>
            {showHistory ? (
              <ChevronUp className="h-5 w-5 text-gray-400" />
            ) : (
              <ChevronDown className="h-5 w-5 text-gray-400" />
            )}
          </button>

          {showHistory && application.statusHistory && application.statusHistory.length > 0 && (
            <div className="mt-4 space-y-3">
              {application.statusHistory.map((history, index) => (
                <div key={index} className="flex items-start border-l-2 border-gray-200 pl-4 py-2">
                  <div className="flex-1">
                    <div className="flex items-center justify-between">
                      <span className={`px-2 py-1 rounded text-xs font-semibold ${getStatusColor(history.status)}`}>
                        {history.status.replace('_', ' ')}
                      </span>
                      <span className="text-xs text-gray-500">
                        {formatDateTime(history.createdAt)}
                      </span>
                    </div>
                    {history.notes && (
                      <p className="text-sm text-gray-600 mt-1">{history.notes}</p>
                    )}
                    {history.updatedBy && (
                      <div className="flex items-center mt-1 text-xs text-gray-500">
                        <User className="h-3 w-3 mr-1" />
                        Updated by Admin
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}

          {showHistory && (!application.statusHistory || application.statusHistory.length === 0) && (
            <p className="text-gray-600 mt-4">No status history available</p>
          )}
        </div>

        {/* Action Buttons */}
        <div className="flex justify-between mt-8">
          <button
            onClick={() => navigate('/services/visa')}
            className="px-6 py-3 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
          >
            Back to Visa Services
          </button>

          {application.status !== 'APPROVED' && application.status !== 'REJECTED' && (
            <button
              onClick={() => navigate(`/visa/application/${applicationId}`)}
              className="px-6 py-3 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700"
            >
              Edit Application
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default VisaStatusTracker;
